import React, { useState } from 'react';
import { useApiData, useAuthenticatedFetch } from '../../api/client';
import type { DastarkhanUser, PaginatedResponse } from '../../api/dastarkhan';
import { usePermissions } from '../../hooks/usePermissions';
import {
  Table,
  Button,
  Card,
  Stack,
  Flex,
  useTheme,
  spacing,
} from '@fti/design-system';

export const UserManagement: React.FC = () => {
  const { theme } = useTheme();
  const { isAdmin } = usePermissions();
  const [page, setPage] = useState(1);
  const { data, loading, error, refetch } = useApiData<PaginatedResponse<DastarkhanUser>>(
    `/dastarkhan/users?page=${page}&per_page=50`
  );
  const { fetchData } = useAuthenticatedFetch<DastarkhanUser>();
  const [actionError, setActionError] = useState<string | null>(null);

  const users = data?.items ?? [];
  const total = data?.total ?? 0;

  const handleRoleChange = async (userId: string, currentRole: string) => {
    const newRole = currentRole === 'admin' ? 'member' : 'admin';
    setActionError(null);
    try {
      await fetchData(`/dastarkhan/users/${userId}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ role: newRole }),
      });
      refetch();
    } catch {
      setActionError('Failed to update role');
    }
  };

  if (!isAdmin) {
    return (
      <Card>
        <Card.Body>
          <p style={{ color: theme.colors.text.secondary }}>Admin access required.</p>
        </Card.Body>
      </Card>
    );
  }

  const columns = [
    { key: 'email', header: 'Email' },
    { key: 'display_name', header: 'Name' },
    {
      key: 'role',
      header: 'Role',
      render: (_value: unknown, row: Record<string, unknown>) => {
        const u = row as unknown as DastarkhanUser;
        return (
          <span style={{
            padding: `${spacing.xs} ${spacing.sm}`,
            borderRadius: '4px',
            fontSize: '0.8rem',
            fontWeight: 600,
            background: u.role === 'admin' ? theme.colors.semantic.warning.light : theme.colors.background.paper,
            color: u.role === 'admin' ? theme.colors.semantic.warning.default : theme.colors.text.secondary,
          }}>
            {u.role}
          </span>
        );
      },
    },
    {
      key: 'is_active',
      header: 'Active',
      render: (_value: unknown, row: Record<string, unknown>) => <>{(row as unknown as DastarkhanUser).is_active ? 'Yes' : 'No'}</>,
    },
    {
      key: 'id',
      header: 'Actions',
      render: (_value: unknown, row: Record<string, unknown>) => {
        const u = row as unknown as DastarkhanUser;
        return (
          <Button variant="outline" size="sm" onClick={() => handleRoleChange(u.id, u.role)}>
            {u.role === 'admin' ? 'Demote' : 'Promote'}
          </Button>
        );
      },
    },
  ];

  return (
    <Stack gap="md">
      <Flex justify="between" align="center">
        <h2 style={{ margin: 0, fontSize: '1.2rem', color: theme.colors.text.primary }}>User Management</h2>
        <span style={{ fontSize: '0.85rem', color: theme.colors.text.secondary }}>{total} users</span>
      </Flex>
      {(error || actionError) && (
        <div style={{ color: theme.colors.semantic.error.default, fontSize: '0.85rem' }}>
          {actionError || error?.message}
        </div>
      )}
      <Table data={users as unknown as Record<string, unknown>[]} columns={columns} loading={loading} emptyMessage="No users found" />
      {total > 50 && (
        <Flex justify="center" gap="sm">
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>Previous</Button>
          <span style={{ fontSize: '0.85rem', color: theme.colors.text.secondary, alignSelf: 'center' }}>Page {page} of {Math.ceil(total / 50)}</span>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page * 50 >= total}>Next</Button>
        </Flex>
      )}
    </Stack>
  );
};
